import type { MatrixRow, Option, OptionId } from "@/components/interview/types";
import { notNull } from "@/utils/helpers";

// Composite ID encoding: "{rowId}:{choiceId}"
// UUIDs never contain colons, so this separator is safe.
export const encodeSelection = (
  rowId: OptionId,
  choiceId: OptionId
): OptionId => `${rowId}:${choiceId}` as OptionId;

export const decodeSelection = (
  id: OptionId
): { rowId: OptionId; colId: OptionId } | null => {
  const sep = id.indexOf(":");
  if (sep < 0) return null;
  return {
    rowId: id.slice(0, sep) as OptionId,
    colId: id.slice(sep + 1) as OptionId,
  };
};

export const getSelectionsForRow = (
  selectedOptionIds: OptionId[],
  rowId: OptionId
): OptionId[] => {
  const prefix = `${rowId}:`;
  return selectedOptionIds
    .filter((id) => id.startsWith(prefix))
    .map((id) => id.slice(prefix.length) as OptionId);
};

export const hasAllMatrixRowsFilled = ({
  rows,
  selectedOptionIds,
}: {
  rows: MatrixRow[];
  selectedOptionIds: OptionId[];
}): boolean =>
  rows.every(
    (row) => getSelectionsForRow(selectedOptionIds, row.id).length > 0
  );

// Column N stays English: this feeds persisted rawInput, which must round-trip identically.
export const getSelectedChoiceTextsForRow = ({
  rowId,
  choices,
  selectedOptionIds,
}: {
  rowId: OptionId;
  choices: Option[];
  selectedOptionIds: OptionId[];
}): string[] => {
  const selectedChoiceIds = getSelectionsForRow(selectedOptionIds, rowId);
  return selectedChoiceIds.map((cId, j) => {
    const colIndex = choices.findIndex((c) => c.id === cId);
    const choice = colIndex >= 0 ? choices[colIndex] : undefined;
    return choice?.text || `Column ${colIndex >= 0 ? colIndex + 1 : j + 1}`;
  });
};

export const formatMatrixSelections = ({
  rows,
  choices,
  selectedOptionIds,
}: {
  rows: MatrixRow[];
  choices: Option[];
  selectedOptionIds: OptionId[];
}): string[] =>
  rows
    .map((row, i) => {
      const selectedTexts = getSelectedChoiceTextsForRow({
        rowId: row.id,
        choices,
        selectedOptionIds,
      }).filter(Boolean);
      if (selectedTexts.length === 0) return null;
      const rowLabel = row.text || `Row ${i + 1}`;
      return `${rowLabel}: ${selectedTexts.join(", ")}`;
    })
    .filter(notNull);

// Uses " | " to match the joiner used by getUserMessage for matrix questions,
// so rawInput round-trips without being re-appended as "different" text.
export const formatMatrixAnswer = (args: {
  rows: MatrixRow[];
  choices: Option[];
  selectedOptionIds: OptionId[];
}): string => formatMatrixSelections(args).join(" | ");
